import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { EntitySuggestionsProvider } from '@/modules/aiSuggestions/context/EntitySuggestions.provider';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EntityDetailsAwareRoute } from '@/modules/entities/contexts/entityDetails/EntityDetailsAwareRoute';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenModal';
import { useEditEntityTrowser } from '@/modules/entities/EditEntitySplitScreen/hooks/useEditEntityTrowser';
import { EstateOnboardingUnacknowledgedSuggestionsV2Document } from '@/modules/estate/estateAIOnboarding/graphql/EstateOnboardingUnacknowledgedSuggestionsV2.generated';
import { EstatePageDataDocument } from '@/modules/estate/graphql/EstatePageData.generated';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';

import { ReviewSuggestions } from './ReviewSuggestions';
import { ReviewSuggestionCountsDocument } from './ReviewSuggestions.generated';

interface ReviewSuggestionsTrowserProps {
  onClose: () => void;
}

export function ReviewSuggestionsTrowser({
  onClose,
}: ReviewSuggestionsTrowserProps) {
  const apolloClient = useApolloClient();
  const reviewSuggestionsTrowser = useTrowser(TrowserKey.ReviewSuggestions);
  const editEntityTrowser = useEditEntityTrowser();
  const refetchSuggestions = useCallback(() => {
    void apolloClient.refetchQueries({
      include: [
        ReviewSuggestionCountsDocument,
        EstatePageDataDocument,
        EstateOnboardingUnacknowledgedSuggestionsV2Document,
      ],
    });
  }, [apolloClient]);

  return (
    <LuminaryThemeProvider>
      <FullScreenModal
        trowserKey={TrowserKey.ReviewSuggestions}
        isOpen
        onClose={onClose}
      >
        <ReviewSuggestions onClose={onClose} />
      </FullScreenModal>
      {reviewSuggestionsTrowser.isOpen && editEntityTrowser.entityId && (
        <EntityDetailsAwareRoute entityId={editEntityTrowser.entityId}>
          <EntitySuggestionsProvider>
            <DispositiveProvisionSuggestionsProvider
              targetId={editEntityTrowser.entityId}
            >
              <EditEntityTrowserGate
                requestedEntityId={editEntityTrowser.entityId}
                section={editEntityTrowser.section ?? undefined}
                onClose={editEntityTrowser.close}
                onAfterSave={refetchSuggestions}
              />
            </DispositiveProvisionSuggestionsProvider>
          </EntitySuggestionsProvider>
        </EntityDetailsAwareRoute>
      )}
    </LuminaryThemeProvider>
  );
}

function EditEntityTrowserGate({
  requestedEntityId,
  section,
  onClose,
  onAfterSave,
}: {
  requestedEntityId: string;
  section?: EditEntitySection;
  onClose: () => void;
  onAfterSave: () => void;
}) {
  const { entityId, entityNotFound, entityType } = useEntityDetailsContext();

  useEffect(() => {
    if (entityNotFound) onClose();
  }, [entityNotFound, onClose]);

  if (entityId !== requestedEntityId || !entityType) return null;

  return (
    <EditEntitySplitScreenModal
      entityType={entityType}
      initialSection={section ?? EditEntitySection.BASIC_INFORMATION}
      isOpen
      onClose={onClose}
      onAfterSave={onAfterSave}
    />
  );
}
